/*
 * Objects — snapping and the exact-position readout while moving / resizing (Phase 61).
 *
 *  formatMeasure(px, unit)        "1.250 in" / "3.175 cm" / "90.00 pt" / "120.0 px" — to the thousandth of an inch
 *  snapObjectMove(rect, ctx)      where a dragged object lands: its left / centre / right (top / middle / bottom) edge jumps
 *                                 to a ruler guide, margin, column / fold line, the page centre or edges, or another object's
 *                                 edge / centre when within OBJECT_SNAP_SCREEN_PX on screen; otherwise the grid when it is on.
 *  snapObjectResize(start, handle, dx, dy, opts, ctx)
 *                                 the same for the edges a resize handle drags (the opposite edge stays put)
 *  snapChipText(snap)             "Left on margin", "Centre on page centre", "Right on text box edge" — the readout chip
 *
 * Both snap functions return { x, y, width, height, snaps: { x, y }, guides }: `snaps` names what each axis locked to (null
 * when it did not), `guides` are the alignment lines the workspace draws ({ orientation, position, type }).
 *
 * ctx: { guides, pageWidth, pageHeight, objects, skipIds, snapToGuides, alignToObjects, snapToGrid, gridSpacing, zoom, free }
 *   guides          ruler / margin / column / row / fold / panel guides (state.guides + derived panel guides)
 *   objects         the other objects on the page (Align To Objects); `skipIds` are the ones being dragged
 *   snapToGuides    Page Design ▸ Align To ▸ Guides        alignToObjects  Page Design ▸ Align To ▸ Objects (page centre / edges too)
 *   zoom            screen px per page px (the tolerance is measured on screen)       free  Alt held — no snapping at all
 */
const DPI = 96;
/** Screen pixels within which a dragged edge jumps to a guide / the page centre / another object's edge. */
export const OBJECT_SNAP_SCREEN_PX = 8;
const MIN_SIZE = 10;

const DIGITS = { in: 3, cm: 3, mm: 2, pt: 2, px: 1 };
export const measureDigits = (unit) => DIGITS[unit] ?? 3;
export const toUnit = (px, unit = 'in') => (unit === 'cm' ? (px / DPI) * 2.54 : unit === 'mm' ? (px / DPI) * 25.4 : unit === 'in' ? px / DPI : unit === 'pt' ? (px * 72) / DPI : px);

/** Length in the document unit at thousandth-of-an-inch precision (never "-0.000"). */
export const formatMeasure = (px, unit = 'in') => {
  const v = Number.isFinite(px) ? toUnit(px, unit) : 0;
  let s = v.toFixed(measureDigits(unit));
  if (/^-0(\.0+)?$/.test(s)) s = s.slice(1);
  return `${s} ${unit}`;
};

const TYPE_NAME = { textbox: 'text box', text: 'text box', image: 'picture', shape: 'shape', line: 'line', table: 'table', group: 'group' };
export const objectTypeName = (o) => TYPE_NAME[o && o.type] || 'object';
const GUIDE_LABEL = { margin: 'margin', grid: 'layout guide', fold: 'fold line', panel: 'panel margin', center: 'panel centre' };
// edges of the dragged object, per axis: start (left / top), end (right / bottom), mid (centre / middle)
const EDGE_NAME = { x: { start: 'Left', end: 'Right', mid: 'Centre' }, y: { start: 'Top', end: 'Bottom', mid: 'Middle' } };

/**
 * Snap targets along one axis. `edges` says which edges of the dragged object a target attracts: guides pull every edge,
 * page edges / object edges pull the outer edges, the page centre / object centres pull the centre (Publisher's rules).
 */
const axisTargets = (axis, ctx) => {
  const { guides = [], pageWidth = 0, pageHeight = 0, objects = [], skipIds = [], snapToGuides = true, alignToObjects = true } = ctx;
  const vertical = axis === 'x'; // x positions are matched against vertical lines
  const orientation = vertical ? 'vertical' : 'horizontal';
  const pageSize = vertical ? pageWidth : pageHeight;
  const t = [];
  if (snapToGuides) {
    guides.forEach((g) => {
      if (!g || g.orientation !== orientation || !Number.isFinite(g.position)) return;
      t.push({ position: g.position, kind: g.kind === 'margin' ? 'margin' : 'guide', label: GUIDE_LABEL[g.kind] || 'guide', lineType: 'user-guide', edges: ['start', 'end', 'mid'] });
    });
  }
  if (alignToObjects) {
    if (pageSize > 0) {
      t.push({ position: pageSize / 2, kind: 'centre', label: 'page centre', lineType: 'center', edges: ['mid'] });
      t.push({ position: 0, kind: 'edge', label: 'page edge', lineType: 'edge', edges: ['start', 'end'] });
      t.push({ position: pageSize, kind: 'edge', label: 'page edge', lineType: 'edge', edges: ['start', 'end'] });
    }
    objects.forEach((o) => {
      if (!o || skipIds.includes(o.id) || !Number.isFinite(o.x) || !Number.isFinite(o.y)) return;
      const name = objectTypeName(o);
      const a = vertical ? o.x : o.y, size = vertical ? o.width || 0 : o.height || 0;
      t.push({ position: a, kind: 'object', label: `${name} edge`, lineType: 'object-edge', edges: ['start', 'end'] });
      t.push({ position: a + size, kind: 'object', label: `${name} edge`, lineType: 'object-edge', edges: ['start', 'end'] });
      t.push({ position: a + size / 2, kind: 'object', label: `${name} centre`, lineType: 'object-center', edges: ['mid'] });
    });
  }
  return t;
};

const tolerance = (zoom) => OBJECT_SNAP_SCREEN_PX / Math.max(0.05, Number.isFinite(zoom) && zoom > 0 ? zoom : 1);

const describe = (axis, edge, target) => ({ axis, edge: EDGE_NAME[axis][edge], kind: target.kind, label: target.label, position: target.position, lineType: target.lineType });

/** Nearest target (within tol) for the given edges; { shift, snap } or null. Ties keep the earlier edge / target. */
const nearest = (axis, edges, targets, tol) => {
  let best = null;
  edges.forEach(([edge, value]) => {
    targets.forEach((t) => {
      if (!t.edges.includes(edge)) return;
      const d = Math.abs(t.position - value);
      if (d <= tol && (!best || d < best.d)) best = { d, shift: t.position - value, snap: describe(axis, edge, t) };
    });
  });
  return best;
};

const alignmentLines = (snaps) => ['x', 'y'].filter((a) => snaps[a] && snaps[a].lineType).map((a) => ({ orientation: a === 'x' ? 'vertical' : 'horizontal', position: snaps[a].position, type: snaps[a].lineType }));

/** Where a moved object lands (its size does not change). */
export const snapObjectMove = (rect, ctx = {}) => {
  const { x, y, width, height } = rect;
  const base = { x, y, width, height };
  if (ctx.free) return { ...base, snaps: { x: null, y: null }, guides: [] };
  const tol = tolerance(ctx.zoom);
  const grid = ctx.snapToGrid && ctx.gridSpacing > 0 ? ctx.gridSpacing : 0;
  const out = { ...base, snaps: { x: null, y: null } };
  [['x', x, width], ['y', y, height]].forEach(([axis, start, size]) => {
    const hit = nearest(axis, [['start', start], ['end', start + size], ['mid', start + size / 2]], axisTargets(axis, ctx), tol);
    if (hit) { out[axis] = start + hit.shift; out.snaps[axis] = hit.snap; }
    else if (grid) { out[axis] = Math.round(start / grid) * grid; out.snaps[axis] = { axis, edge: EDGE_NAME[axis].start, kind: 'grid', label: 'grid', position: out[axis], lineType: null }; }
  });
  out.guides = alignmentLines(out.snaps);
  return out;
};

/**
 * Where a resized object lands. `start` is the frame when the handle was grabbed, `handle` one of nw n ne w e sw s se,
 * dx / dy the pointer travel in page px. opts: { keepRatio, fromCentre, free } — Shift / pictures keep the ratio, Alt
 * resizes from the centre (the dragged edge follows the pointer, the opposite edge mirrors it) and, as everywhere,
 * switches snapping off.
 */
export const snapObjectResize = (start, handle = '', dx = 0, dy = 0, opts = {}, ctx = {}) => {
  const { keepRatio = false, fromCentre = false, free = false } = opts;
  let { x, y, width, height } = start;
  const right0 = start.x + start.width, bottom0 = start.y + start.height;
  const ratio = start.width / Math.max(1, start.height);
  const east = handle.includes('e'), west = handle.includes('w'), south = handle.includes('s'), north = handle.includes('n');
  // from the centre the dragged edge still follows the pointer and the opposite edge mirrors it (Publisher)
  const k = fromCentre ? 2 : 1;
  if (east) width = Math.max(MIN_SIZE, start.width + dx * k);
  if (west) { width = Math.max(MIN_SIZE, start.width - dx * k); x = right0 - width; }
  if (south) height = Math.max(MIN_SIZE, start.height + dy * k);
  if (north) { height = Math.max(MIN_SIZE, start.height - dy * k); y = bottom0 - height; }
  const snaps = { x: null, y: null };
  if (!free && !fromCentre) {
    const tol = tolerance(ctx.zoom);
    const grid = ctx.snapToGrid && ctx.gridSpacing > 0 ? ctx.gridSpacing : 0;
    const gridSnap = (axis, edge, v) => ({ pos: Math.round(v / grid) * grid, snap: { axis, edge: EDGE_NAME[axis][edge], kind: 'grid', label: 'grid', position: Math.round(v / grid) * grid, lineType: null } });
    if (east || west) {
      const edge = east ? 'end' : 'start', value = east ? x + width : x;
      const hit = nearest('x', [[edge, value]], axisTargets('x', ctx), tol);
      const target = hit ? { pos: hit.snap.position, snap: hit.snap } : grid ? gridSnap('x', edge, value) : null;
      if (target) {
        if (east && target.pos - x >= MIN_SIZE) { width = target.pos - x; snaps.x = target.snap; }
        else if (west && right0 - target.pos >= MIN_SIZE) { x = target.pos; width = right0 - x; snaps.x = target.snap; }
      }
    }
    if (south || north) {
      const edge = south ? 'end' : 'start', value = south ? y + height : y;
      const hit = nearest('y', [[edge, value]], axisTargets('y', ctx), tol);
      const target = hit ? { pos: hit.snap.position, snap: hit.snap } : grid ? gridSnap('y', edge, value) : null;
      if (target) {
        if (south && target.pos - y >= MIN_SIZE) { height = target.pos - y; snaps.y = target.snap; }
        else if (north && bottom0 - target.pos >= MIN_SIZE) { y = target.pos; height = bottom0 - y; snaps.y = target.snap; }
      }
    }
  }
  if (keepRatio && handle.length === 2) {
    // a corner drag keeps the ratio: the width leads unless only the height found something to snap to
    if (snaps.y && !snaps.x) { width = Math.max(MIN_SIZE, height * ratio); if (west) x = right0 - width; }
    else { height = Math.max(MIN_SIZE, width / ratio); snaps.y = null; if (north) y = bottom0 - height; }
  }
  if (fromCentre) {
    const cx = start.x + start.width / 2, cy = start.y + start.height / 2;
    x = cx - width / 2; y = cy - height / 2;
  }
  return { x, y, width, height, snaps, guides: alignmentLines(snaps) };
};

/** "Left on margin", "Centre on page centre", "Bottom on picture edge" … */
export const snapChipText = (snap) => (snap ? `${snap.edge} on ${snap.label}` : '');
